import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Building2, User, MessageSquare, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";

const REQ_TYPES = [
  { value: "Tender", label: "Government / Private Tender" },
  { value: "Campaign", label: "OOH Campaign" },
  { value: "Media Buying", label: "Media Buying" },
  { value: "General", label: "General Enquiry" },
];

export default function Contact() {
  useReveal();
  const { hash } = useLocation();
  const [form, setForm] = useState({
    name: "",
    company: "",
    requirement: "",
    message: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  // Smooth-scroll to the form when arriving with #inquiry-form
  useEffect(() => {
    if (hash === "#inquiry-form") {
      const t = setTimeout(() => {
        const el = document.getElementById("inquiry-form");
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 120);
      return () => clearTimeout(t);
    }
  }, [hash]);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Required";
    if (!form.company.trim()) err.company = "Required";
    if (!form.requirement) err.requirement = "Select one";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Valid email required";
    return err;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length) {
      setErrors(err);
      toast.error("Please complete the highlighted fields.");
      return;
    }

    const subject = encodeURIComponent(`[${form.requirement}] New enquiry from ${form.name}`);
    const bodyLines = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone || "—"}`,
      `Requirement: ${form.requirement}`,
      ``,
      `Message:`,
      form.message || "(no message provided)",
      ``,
      `— Sent from advedaads.com contact form`,
    ].join("\n");
    const body = encodeURIComponent(bodyLines);
    const mailto = `mailto:business@advedaads.com?cc=info@advedaads.com&subject=${subject}&body=${body}`;

    window.location.href = mailto;
    toast.success("Opening your email client…", {
      description: "We've pre-filled your enquiry to business@advedaads.com",
    });

    setForm({ name: "", company: "", requirement: "", message: "", email: "", phone: "" });
  };

  return (
    <div data-testid="page-contact" className="bg-[#0a0a0a] text-white">
      {/* Hero */}
      <section className="relative pt-28 pb-10 md:pt-32 md:pb-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6 reveal">
            <span className="w-10 h-px bg-[#d40202]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">
              / Get in touch
            </span>
          </div>
          <h1
            data-testid="contact-heading"
            className="font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl reveal"
          >
            Let&apos;s talk <span className="text-[#d40202]">OOH.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed reveal">
            Whether you&apos;re a government department floating a tender or a brand planning a
            launch, share a few details and our team will revert within one business day.
          </p>
        </div>
      </section>

      {/* Split: Details + Form */}
      <section className="relative border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Details */}
          <div className="lg:col-span-5 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Head Office</div>
            <h2 className="mt-6 font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] tracking-tighter">
              New Delhi<span className="text-[#d40202]">.</span>
            </h2>

            <div className="mt-10 space-y-8">
              <div data-testid="contact-address-block" className="flex gap-4">
                <MapPin className="w-5 h-5 text-[#d40202] mt-1 shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Address</div>
                  <address className="not-italic text-zinc-200 leading-relaxed">
                    L-80/C, Vijay Vihar Phase 2, <br />
                    Rohini Sector 7, <br />
                    New Delhi
                  </address>
                </div>
              </div>

              <div className="flex gap-4">
                <Phone className="w-5 h-5 text-[#d40202] mt-1 shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Phone</div>
                  <a
                    href="tel:+918601515509"
                    data-testid="contact-phone"
                    className="block text-zinc-200 hover:text-[#d40202] transition-colors text-lg"
                  >
                    +91 86015 15509
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <Mail className="w-5 h-5 text-[#d40202] mt-1 shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Email</div>
                  <a
                    href="mailto:info@advedaads.com"
                    data-testid="contact-email-info"
                    className="block text-zinc-200 hover:text-[#d40202] transition-colors"
                  >
                    info@advedaads.com
                  </a>
                  <a
                    href="mailto:business@advedaads.com"
                    data-testid="contact-email-business"
                    className="block mt-1 text-zinc-200 hover:text-[#d40202] transition-colors"
                  >
                    business@advedaads.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="https://wa.me/918601515509?text=Hi%20AdVeda%2C%20I%27d%20like%20to%20discuss%20a%20project."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="group inline-flex items-center justify-between gap-3 border border-white/15 hover:border-[#25D366] bg-[#0d0d0d] hover:bg-[#0f1a14] px-5 py-4 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" strokeWidth={1.75} />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Chat on</span>
                    <span className="block text-white font-medium">WhatsApp</span>
                  </span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[#25D366] transition-colors" />
              </a>
              <a
                href="tel:+918601515509"
                data-testid="contact-call-cta"
                className="group inline-flex items-center justify-between gap-3 border border-white/15 hover:border-[#d40202] bg-[#0d0d0d] hover:bg-[#160a0a] px-5 py-4 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#d40202]" strokeWidth={1.75} />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Quick call</span>
                    <span className="block text-white font-medium">+91 86015 15509</span>
                  </span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[#d40202] transition-colors" />
              </a>
            </div>

            <div className="mt-8 p-6 md:p-8 border border-white/10 bg-[#0d0d0d]">
              <div className="text-xs uppercase tracking-[0.2em] text-[#d40202] font-bold mb-3">
                / Business hours
              </div>
              <p className="text-zinc-300">
                Monday &mdash; Saturday<br />
                10:00 AM &mdash; 7:00 PM IST
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <form
              id="inquiry-form"
              onSubmit={onSubmit}
              data-testid="inquiry-form"
              noValidate
              className="scroll-mt-24 border border-white/10 bg-[#0d0d0d] p-6 md:p-10"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="w-8 h-px bg-[#d40202]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">
                  / Enquiry Form
                </span>
              </div>
              <h3 className="font-display font-black text-2xl md:text-4xl uppercase tracking-tighter">
                Tell us about your <span className="text-[#d40202]">project.</span>
              </h3>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  id="name"
                  label="Your Name"
                  icon={User}
                  value={form.name}
                  onChange={update("name")}
                  error={errors.name}
                  placeholder="Jane Sharma"
                  testId="input-name"
                />
                <Field
                  id="company"
                  label="Company / Dept."
                  icon={Building2}
                  value={form.company}
                  onChange={update("company")}
                  error={errors.company}
                  placeholder="e.g. DDA / Acme Realty"
                  testId="input-company"
                />
                <Field
                  id="email"
                  label="Email"
                  type="email"
                  icon={Mail}
                  value={form.email}
                  onChange={update("email")}
                  error={errors.email}
                  placeholder="you@company.com"
                  testId="input-email"
                />
                <Field
                  id="phone"
                  label="Phone (optional)"
                  icon={Phone}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98 ..."
                  testId="input-phone"
                />
              </div>

              {/* Requirement radio group */}
              <div className="mt-8">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">
                  Requirement
                </label>
                <div
                  data-testid="requirement-group"
                  className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10"
                >
                  {REQ_TYPES.map((r) => {
                    const active = form.requirement === r.value;
                    return (
                      <button
                        type="button"
                        key={r.value}
                        data-testid={`requirement-option-${r.value.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => {
                          setForm((f) => ({ ...f, requirement: r.value }));
                          setErrors((er) => ({ ...er, requirement: undefined }));
                        }}
                        className={`text-left px-4 py-5 text-sm transition-colors ${
                          active
                            ? "bg-[#d40202] text-white"
                            : "bg-[#0a0a0a] text-zinc-300 hover:bg-[#151515] hover:text-white"
                        }`}
                      >
                        <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-70">
                          {r.value}
                        </div>
                        <div className="mt-1 font-medium">{r.label.replace(r.value, "").trim() || r.label}</div>
                      </button>
                    );
                  })}
                </div>
                {errors.requirement && (
                  <p className="mt-2 text-xs text-[#d40202]">{errors.requirement}</p>
                )}
              </div>

              {/* Message */}
              <div className="mt-8">
                <label
                  htmlFor="message"
                  className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold flex items-center gap-2"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-[#d40202]" /> Brief
                </label>
                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  placeholder="Tender reference, locations, timeline, budget range..."
                  className="mt-3 w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#d40202] focus:ring-1 focus:ring-[#d40202] transition-colors resize-none"
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
                  Submitting opens your email client with the enquiry pre-filled to{" "}
                  <span className="text-white">business@advedaads.com</span>.
                </p>
                <button
                  type="submit"
                  data-testid="submit-inquiry-btn"
                  className="inline-flex items-center justify-center gap-3 bg-[#d40202] hover:bg-white hover:text-black text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
                >
                  Send Enquiry <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, icon: Icon, value, onChange, error, type = "text", placeholder, testId }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold flex items-center gap-2"
      >
        {Icon && <Icon className="w-3.5 h-3.5 text-[#d40202]" />} {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className={`mt-3 w-full bg-transparent border px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 transition-colors ${
          error
            ? "border-[#d40202] focus:border-[#d40202] focus:ring-[#d40202]"
            : "border-white/15 focus:border-[#d40202] focus:ring-[#d40202]"
        }`}
      />
      {error && <p className="mt-2 text-xs text-[#d40202]">{error}</p>}
    </div>
  );
}
